import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useGoogleLogin } from '@react-oauth/google';
import { getApiUrl } from '../config';
import NicknameModal from './NicknameModal';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegisterSuccess: (token: string, user: any) => void;
}

const RegisterModal: React.FC<RegisterModalProps> = ({ isOpen, onClose, onRegisterSuccess }) => {
  const [email, setEmail] = useState('');
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingGoogleData, setPendingGoogleData] = useState<null | { accessToken: string; picture?: string; name?: string }>(null);
  const [showNicknameModal, setShowNicknameModal] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setNickname('');
      setPassword('');
      setPasswordConfirm('');
      setLoading(false);
      setGoogleLoading(false);
      setError('');
      setPendingGoogleData(null);
      setShowNicknameModal(false);
    }
  }, [isOpen]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== passwordConfirm) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(getApiUrl('/api/auth/register'), {
        email,
        nickname,
        password,
      });
      const { user } = response.data;
      localStorage.setItem('user', JSON.stringify(user));
      onRegisterSuccess('', user);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      setError('');
      try {
        const precheckResponse = await axios.post(
          getApiUrl('/api/auth/google'),
          {
            accessToken: tokenResponse.access_token,
            mode: 'precheck',
          },
          { timeout: 12000 }
        );

        // Compatibilidad con versiones antiguas del backend:
        // si responde con user en precheck, significa que ese Gmail ya existe.
        if (precheckResponse?.data?.user || precheckResponse?.data?.available === false) {
          setShowNicknameModal(false);
          setPendingGoogleData(null);
          setError('Este Gmail ya está registrado. Por favor, inicia sesión.');
          return;
        }

        const userInfoResponse = await axios.get('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          withCredentials: false,
        });
        const { name, picture } = userInfoResponse.data;
        setPendingGoogleData({ accessToken: tokenResponse.access_token, picture, name });
        setShowNicknameModal(true);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error al registrarse con Google');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => setError('Error al registrarse con Google'),
  });

  const handleConfirmGoogleNickname = async (selectedNickname: string) => {
    if (!pendingGoogleData) {
      return;
    }

    setGoogleLoading(true);
    setError('');

    try {
      const response = await axios.post(getApiUrl('/api/auth/google'), {
        accessToken: pendingGoogleData.accessToken,
        nickname: selectedNickname,
        mode: 'register',
      });

      const { user } = response.data;
      localStorage.setItem('user', JSON.stringify(user));
      onRegisterSuccess('', user);
      setShowNicknameModal(false);
      setPendingGoogleData(null);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al registrarse con Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
      onClick={() => {
        if (!showNicknameModal) {
          onClose();
        }
      }}
    >
      <NicknameModal
        isOpen={showNicknameModal}
        onClose={() => {
          setShowNicknameModal(false);
          setPendingGoogleData(null);
        }}
        onConfirm={handleConfirmGoogleNickname}
        googleName={pendingGoogleData?.name}
      />
      <div className="card-gaming max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-gaming text-gaming-purple text-lg tracking-wider">REGISTRARSE</h2>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white transition-colors text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleRegister} className="space-y-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded text-sm font-main">
              {error}
            </div>
          )}

          <div>
            <label className="block font-main text-white/70 text-sm mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white font-main focus:outline-none focus:border-gaming-purple"
              required
            />
          </div>

          <div>
            <label className="block font-main text-white/70 text-sm mb-2">Nickname</label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white font-main focus:outline-none focus:border-gaming-purple"
              required
            />
          </div>

          <div>
            <label className="block font-main text-white/70 text-sm mb-2">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white font-main focus:outline-none focus:border-gaming-purple"
              required
            />
          </div>

          <div>
            <label className="block font-main text-white/70 text-sm mb-2">Confirmar contraseña</label>
            <input
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white font-main focus:outline-none focus:border-gaming-purple"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3 font-gaming text-sm tracking-wider disabled:opacity-50"
          >
            {loading ? 'Registrando...' : 'REGISTRARSE'}
          </button>
        </form>

        <div className="my-4 border-t border-white/10"></div>

        <button
          onClick={() => handleGoogleRegister()}
          disabled={googleLoading}
          className="w-full bg-white/5 hover:bg-white/10 border border-white/10 rounded px-3 py-3 text-white font-main text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
          {googleLoading ? 'Conectando...' : 'Registrarse con Google'}
        </button>
      </div>
    </div>
  );
};

export default RegisterModal;
